import type { ReactNode } from 'react';

import {
  coins,
  DEMI_HAUTEUR,
  FACE_DROITE,
  FACE_GAUCHE,
  losange,
  proj,
  pts,
  teinter,
} from './iso.js';

/**
 * Les volumes de la ville.
 *
 * Tout est construit à partir d'un seul solide — le pavé droit — décliné en
 * maisons, immeubles, hangars et wagons. Chaque volume ne montre que ses trois
 * faces visibles, éclairées depuis le haut à gauche : le dessus au plein jour,
 * la face de gauche un peu retirée, celle de droite dans l'ombre. Une seule
 * teinte suffit donc à décrire un bâtiment.
 *
 * Tous ces composants sont décoratifs : ils portent `aria-hidden`, et seul le
 * quartier qui les contient parle aux lecteurs d'écran.
 */

interface Empreinte {
  /** Coin nord de l'empreinte, en tuiles. */
  readonly gx: number;
  readonly gy: number;
  /** Étendue de l'empreinte, en tuiles. */
  readonly l: number;
  readonly p: number;
}

/** L'ombre portée au sol, qui décolle le volume de la parcelle. */
function Ombre({ gx, gy, l, p }: Empreinte) {
  return (
    <polygon
      points={losange(gx - 0.1, gy + 0.25, l + 0.35, p + 0.2)}
      fill="#0F172A"
      opacity="0.1"
    />
  );
}

/**
 * Le pavé droit : l'unité de construction de toute la ville.
 *
 * `h` est une hauteur en pixels, pas en tuiles — c'est ce qu'on règle à l'œil
 * pour qu'un pavillon reste bas et qu'une tour domine le centre.
 */
export function Bloc({
  gx,
  gy,
  l,
  p,
  h,
  teinte,
  toit,
  ombre = true,
}: Empreinte & {
  readonly h: number;
  readonly teinte: string;
  readonly toit?: string;
  readonly ombre?: boolean;
}) {
  const bas = coins(gx, gy, l, p);
  const haut = coins(gx, gy, l, p, h);
  return (
    <>
      {ombre && <Ombre gx={gx} gy={gy} l={l} p={p} />}
      <polygon points={pts(bas.o, bas.s, haut.s, haut.o)} fill={teinter(teinte, FACE_GAUCHE)} />
      <polygon points={pts(bas.s, bas.e, haut.e, haut.s)} fill={teinter(teinte, FACE_DROITE)} />
      <polygon points={pts(haut.n, haut.e, haut.s, haut.o)} fill={toit ?? teinte} />
    </>
  );
}

/**
 * Les fenêtres d'une façade.
 *
 * `face` dit laquelle des deux façades visibles on habille : « gauche » suit
 * l'arête ouest-sud, « droite » l'arête sud-est. Une fenêtre sur cinq reste
 * éteinte même quartier équipé — une ville dont toutes les vitres s'allument en
 * même temps ne ressemble à rien.
 */
function Fenetres({
  gx,
  gy,
  l,
  p,
  h,
  face,
  colonnes,
  rangees,
  allumees,
}: Empreinte & {
  readonly h: number;
  readonly face: 'gauche' | 'droite';
  readonly colonnes: number;
  readonly rangees: number;
  readonly allumees: boolean;
}) {
  const etendue = face === 'gauche' ? l : p;
  const pasH = etendue / colonnes;
  const largeur = pasH * 0.46;
  const pasV = (h - 10) / rangees;
  const hauteur = pasV * 0.5;

  const carreaux: ReactNode[] = [];
  for (let i = 0; i < colonnes; i += 1) {
    for (let j = 0; j < rangees; j += 1) {
      const t = (i + 0.5) * pasH - largeur / 2;
      const z = 6 + j * pasV;
      const eteinte = (i * rangees + j) % 5 === 2;
      const point = (dt: number, dz: number) =>
        face === 'gauche'
          ? proj(gx + t + dt, gy + p, z + dz)
          : proj(gx + l, gy + p - t - dt, z + dz);
      carreaux.push(
        <polygon
          key={`${face}-${i}-${j}`}
          points={pts(point(0, 0), point(largeur, 0), point(largeur, hauteur), point(0, hauteur))}
          fill={allumees && !eteinte ? '#FDE68A' : '#BFD3E6'}
          opacity={allumees && !eteinte ? 0.95 : 0.7}
        />,
      );
    }
  }
  return <>{carreaux}</>;
}

/** Un toit à deux pentes, dont le faîtage suit l'axe `gx`. */
function ToitPente({
  gx,
  gy,
  l,
  p,
  h,
  hp,
  teinte,
}: Empreinte & { readonly h: number; readonly hp: number; readonly teinte: string }) {
  const bas = coins(gx, gy, l, p, h);
  const faite1 = proj(gx, gy + p / 2, h + hp);
  const faite2 = proj(gx + l, gy + p / 2, h + hp);
  return (
    <>
      {/* Versant arrière : on n'en voit que le liseré au-dessus du faîtage. */}
      <polygon points={pts(bas.n, bas.e, faite2, faite1)} fill={teinter(teinte, 1.08)} />
      {/* Pignon de droite. */}
      <polygon points={pts(bas.e, bas.s, faite2)} fill={teinter(teinte, 0.66)} />
      {/* Versant avant, le grand pan qu'on regarde. */}
      <polygon points={pts(bas.o, bas.s, faite2, faite1)} fill={teinter(teinte, 0.9)} />
    </>
  );
}

/** Un pavillon : un corps bas, un toit pentu, une porte. */
export function Maison({
  gx,
  gy,
  teinte,
  toit,
  allumee = false,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly teinte: string;
  readonly toit: string;
  readonly allumee?: boolean;
}) {
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={2} p={1.6} h={26} teinte={teinte} />
      <ToitPente gx={gx - 0.14} gy={gy - 0.14} l={2.28} p={1.88} h={26} hp={10} teinte={toit} />
      <Fenetres
        gx={gx}
        gy={gy}
        l={2}
        p={1.6}
        h={26}
        face="gauche"
        colonnes={3}
        rangees={1}
        allumees={allumee}
      />
      <polygon
        points={pts(
          proj(gx + 2, gy + 1.05, 0),
          proj(gx + 2, gy + 0.55, 0),
          proj(gx + 2, gy + 0.55, 18),
          proj(gx + 2, gy + 1.05, 18),
        )}
        fill={teinter(toit, 0.55)}
      />
    </g>
  );
}

/** Un immeuble : un corps haut, deux façades vitrées, un édicule sur le toit. */
export function Immeuble({
  gx,
  gy,
  h,
  teinte,
  allume = false,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly h: number;
  readonly teinte: string;
  readonly allume?: boolean;
}) {
  const rangees = Math.max(2, Math.round(h / 22));
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={1.7} p={1.7} h={h} teinte={teinte} toit={teinter(teinte, 1.1)} />
      <Fenetres
        gx={gx}
        gy={gy}
        l={1.7}
        p={1.7}
        h={h}
        face="gauche"
        colonnes={3}
        rangees={rangees}
        allumees={allume}
      />
      <Fenetres
        gx={gx}
        gy={gy}
        l={1.7}
        p={1.7}
        h={h}
        face="droite"
        colonnes={3}
        rangees={rangees}
        allumees={allume}
      />
      <Bloc
        gx={gx + 0.5}
        gy={gy + 0.5}
        l={0.7}
        p={0.7}
        h={h + 12}
        teinte={teinter(teinte, 0.88)}
        ombre={false}
      />
    </g>
  );
}

/** Un hangar d'atelier : long, bas, toit de tôle à deux pentes. */
export function Hangar({
  gx,
  gy,
  teinte = '#E7E5E4',
}: {
  readonly gx: number;
  readonly gy: number;
  readonly teinte?: string;
}) {
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={3.2} p={1.8} h={22} teinte={teinte} />
      <ToitPente gx={gx - 0.12} gy={gy - 0.12} l={3.44} p={2.04} h={22} hp={13} teinte="#94A3B8" />
      {/* La grande porte coulissante, sur le pignon de droite. */}
      <polygon
        points={pts(
          proj(gx + 3.2, gy + 1.35, 0),
          proj(gx + 3.2, gy + 0.45, 0),
          proj(gx + 3.2, gy + 0.45, 16),
          proj(gx + 3.2, gy + 1.35, 16),
        )}
        fill="#475569"
      />
      <Fenetres
        gx={gx}
        gy={gy}
        l={3.2}
        p={1.8}
        h={22}
        face="gauche"
        colonnes={5}
        rangees={1}
        allumees={false}
      />
    </g>
  );
}

/** L'école : un corps clair, un bandeau coloré, un préau. */
export function Ecole({
  gx,
  gy,
  allumee = false,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly allumee?: boolean;
}) {
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={2.8} p={1.8} h={36} teinte="#FDE9CF" toit="#C2410C" />
      <Fenetres
        gx={gx}
        gy={gy}
        l={2.8}
        p={1.8}
        h={36}
        face="gauche"
        colonnes={5}
        rangees={2}
        allumees={allumee}
      />
      <Fenetres
        gx={gx}
        gy={gy}
        l={2.8}
        p={1.8}
        h={36}
        face="droite"
        colonnes={3}
        rangees={2}
        allumees={allumee}
      />
      {/* Le préau accolé, côté cour. */}
      <Bloc gx={gx + 2.8} gy={gy + 0.25} l={1} p={1.3} h={15} teinte="#FCD34D" toit="#D97706" />
    </g>
  );
}

/** Le gymnase : une halle à toit arrondi. */
export function Gymnase({ gx, gy }: { readonly gx: number; readonly gy: number }) {
  const bas = coins(gx, gy, 2.4, 1.8, 20);
  const faite1 = proj(gx, gy + 0.9, 40);
  const faite2 = proj(gx + 2.4, gy + 0.9, 40);
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={2.4} p={1.8} h={20} teinte="#DBEAFE" />
      <path
        d={`M ${bas.o.x} ${bas.o.y} Q ${faite1.x} ${faite1.y - 6} ${faite1.x} ${faite1.y}
            L ${faite2.x} ${faite2.y} Q ${faite2.x} ${faite2.y - 6} ${bas.s.x} ${bas.s.y} Z`}
        fill="#93C5FD"
      />
      <path
        d={`M ${bas.e.x} ${bas.e.y} Q ${faite2.x} ${faite2.y - 6} ${faite2.x} ${faite2.y}
            L ${bas.s.x} ${bas.s.y} Z`}
        fill="#60A5FA"
      />
    </g>
  );
}

/** La mairie : un perron, des colonnes, un fronton et le drapeau. */
export function Mairie({
  gx,
  gy,
  allumee = false,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly allumee?: boolean;
}) {
  const mat = proj(gx + 1.7, gy + 1.6, 48);
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={3.4} p={2.2} h={42} teinte="#FEF3C7" toit="#B45309" />
      <Fenetres
        gx={gx}
        gy={gy}
        l={3.4}
        p={2.2}
        h={42}
        face="gauche"
        colonnes={6}
        rangees={2}
        allumees={allumee}
      />
      {/* Les colonnes du perron, posées devant la façade de gauche. */}
      {[0.4, 1.2, 2.0, 2.8].map((t) => (
        <polygon
          key={t}
          points={pts(
            proj(gx + t, gy + 2.32, 0),
            proj(gx + t + 0.24, gy + 2.32, 0),
            proj(gx + t + 0.24, gy + 2.32, 31),
            proj(gx + t, gy + 2.32, 31),
          )}
          fill="#FFFBEB"
        />
      ))}
      {/* Le fronton. */}
      <polygon
        points={pts(
          proj(gx - 0.1, gy + 2.4, 31),
          proj(gx + 3.5, gy + 2.4, 31),
          proj(gx + 1.7, gy + 2.4, 46),
        )}
        fill="#D97706"
      />
      <line x1={mat.x} y1={mat.y} x2={mat.x} y2={mat.y - 26} stroke="#78716C" strokeWidth="2" />
      <polygon
        points={`${mat.x},${mat.y - 26} ${mat.x + 18},${mat.y - 21} ${mat.x},${mat.y - 16}`}
        fill="#2563EB"
      />
    </g>
  );
}

/** La gare : un corps blanc, un large auvent, une horloge. */
export function Gare({
  gx,
  gy,
  allumee = false,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly allumee?: boolean;
}) {
  const horloge = proj(gx + 3.4, gy + 1, 36);
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={3.4} p={2} h={40} teinte="#F8FAFC" toit="#4338CA" />
      <Fenetres
        gx={gx}
        gy={gy}
        l={3.4}
        p={2}
        h={40}
        face="gauche"
        colonnes={6}
        rangees={2}
        allumees={allumee}
      />
      {/* L'auvent qui déborde sur le parvis. */}
      <polygon points={losange(gx - 0.4, gy + 2, 4.2, 1.5, 28)} fill="#4F46E5" />
      <polygon points={losange(gx - 0.4, gy + 2, 4.2, 1.5, 25)} fill="#3730A3" />
      {[0.2, 1.9, 3.6].map((t) => (
        <line
          key={t}
          x1={proj(gx - 0.3 + t, gy + 3.4, 0).x}
          y1={proj(gx - 0.3 + t, gy + 3.4, 0).y}
          x2={proj(gx - 0.3 + t, gy + 3.4, 25).x}
          y2={proj(gx - 0.3 + t, gy + 3.4, 25).y}
          stroke="#6366F1"
          strokeWidth="3"
        />
      ))}
      <circle cx={horloge.x} cy={horloge.y} r="7" fill="#FFFFFF" stroke="#312E81" strokeWidth="2" />
      <line
        x1={horloge.x}
        y1={horloge.y}
        x2={horloge.x}
        y2={horloge.y - 4}
        stroke="#312E81"
        strokeWidth="1.5"
      />
      <line
        x1={horloge.x}
        y1={horloge.y}
        x2={horloge.x + 3}
        y2={horloge.y}
        stroke="#312E81"
        strokeWidth="1.5"
      />
    </g>
  );
}

/** Un arbre : un tronc et deux houppiers. */
export function Arbre({
  gx,
  gy,
  taille = 1,
  teinte = '#16A34A',
}: {
  readonly gx: number;
  readonly gy: number;
  readonly taille?: number;
  readonly teinte?: string;
}) {
  const pied = proj(gx, gy);
  const r = 11 * taille;
  const tronc = 16 * taille;
  return (
    <g aria-hidden="true">
      <ellipse
        cx={pied.x}
        cy={pied.y + 2}
        rx={r * 0.9}
        ry={r * 0.42}
        fill="#0F172A"
        opacity="0.12"
      />
      <rect x={pied.x - 2} y={pied.y - tronc} width="4" height={tronc} fill="#78350F" />
      <circle cx={pied.x} cy={pied.y - tronc - r * 0.55} r={r} fill={teinte} />
      <circle
        cx={pied.x - r * 0.45}
        cy={pied.y - tronc - r * 0.95}
        r={r * 0.72}
        fill={teinter(teinte, 1.2)}
      />
    </g>
  );
}

/** Un buisson : la même idée, en plus bas. */
export function Buisson({
  gx,
  gy,
  teinte = '#4D7C0F',
}: {
  readonly gx: number;
  readonly gy: number;
  readonly teinte?: string;
}) {
  const pied = proj(gx, gy);
  return (
    <g aria-hidden="true">
      <ellipse cx={pied.x} cy={pied.y + 1} rx="10" ry="4" fill="#0F172A" opacity="0.1" />
      <circle cx={pied.x - 4} cy={pied.y - 5} r="6" fill={teinte} />
      <circle cx={pied.x + 4} cy={pied.y - 5} r="6" fill={teinte} />
      <circle cx={pied.x} cy={pied.y - 9} r="7" fill={teinter(teinte, 1.18)} />
    </g>
  );
}

/** L'étang du parc. */
export function Etang({ gx, gy, l, p }: Empreinte) {
  return (
    <g aria-hidden="true">
      <polygon points={losange(gx, gy, l, p)} fill="#38BDF8" opacity="0.85" />
      <polygon
        points={losange(gx + 0.25, gy + 0.25, l - 0.5, p - 0.5)}
        fill="#7DD3FC"
        opacity="0.8"
      />
    </g>
  );
}

/**
 * Un lampadaire.
 *
 * Éteint, c'est un mât gris. Allumé, il projette un cône de lumière et pose une
 * flaque claire sur la chaussée : c'est la récompense du chapitre 2, et elle
 * doit se voir depuis le fond de la classe.
 */
export function Lampadaire({
  gx,
  gy,
  allume = false,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly allume?: boolean;
}) {
  const pied = proj(gx, gy);
  const tete = { x: pied.x + 9, y: pied.y - 46 };
  return (
    <g aria-hidden="true">
      <ellipse cx={pied.x} cy={pied.y} rx="7" ry="3" fill="#0F172A" opacity="0.14" />
      {allume && (
        <>
          <polygon
            points={`${tete.x},${tete.y} ${tete.x + 26},${tete.y + 46} ${tete.x - 34},${tete.y + 46}`}
            fill="#FDE047"
            opacity="0.26"
          />
          <ellipse cx={tete.x - 4} cy={pied.y} rx="30" ry="12" fill="#FACC15" opacity="0.4" />
        </>
      )}
      <rect x={pied.x - 1.5} y={pied.y - 46} width="3" height="46" fill="#475569" />
      <path
        d={`M ${pied.x} ${pied.y - 46} q 0 -6 9 -6`}
        fill="none"
        stroke="#475569"
        strokeWidth="3"
      />
      <ellipse cx={tete.x} cy={tete.y} rx="6" ry="3.5" fill={allume ? '#FDE047' : '#94A3B8'} />
      {allume && <circle cx={tete.x} cy={tete.y} r="11" fill="#FDE047" opacity="0.35" />}
    </g>
  );
}

/** Une voiture, vue de trois quarts elle aussi. */
export function Voiture({
  gx,
  gy,
  teinte,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly teinte: string;
}) {
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={0.72} p={0.4} h={8} teinte={teinte} toit={teinter(teinte, 1.12)} />
      <Bloc
        gx={gx + 0.16}
        gy={gy + 0.06}
        l={0.42}
        p={0.28}
        h={14}
        teinte={teinter(teinte, 0.92)}
        toit="#CBD5E1"
        ombre={false}
      />
    </g>
  );
}

/** Une benne de tri, couvercle coloré selon le flux. */
export function Benne({
  gx,
  gy,
  couvercle,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly couvercle: string;
}) {
  return (
    <g aria-hidden="true">
      <Bloc gx={gx} gy={gy} l={0.55} p={0.5} h={13} teinte="#E2E8F0" toit={couvercle} />
    </g>
  );
}

/**
 * Un capteur sur mât — le marqueur « ville connectée ».
 *
 * Ses ondes n'apparaissent que si le quartier est équipé : un capteur qui
 * émet dans un quartier qu'on n'a pas encore instrumenté raconterait le
 * contraire de ce que le chapitre 2 fait faire aux élèves.
 */
export function Capteur({
  gx,
  gy,
  actif = false,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly actif?: boolean;
}) {
  const pied = proj(gx, gy);
  const haut = pied.y - 34;
  return (
    <g aria-hidden="true">
      <ellipse cx={pied.x} cy={pied.y} rx="6" ry="2.5" fill="#0F172A" opacity="0.14" />
      <rect x={pied.x - 1.5} y={haut} width="3" height="34" fill="#64748B" />
      <rect
        x={pied.x - 5}
        y={haut - 9}
        width="10"
        height="10"
        rx="2"
        fill={actif ? '#0EA5E9' : '#94A3B8'}
      />
      {actif &&
        [7, 12, 17].map((r, i) => (
          <path
            key={r}
            d={`M ${pied.x + r * 0.7} ${haut - 4 - r * 0.7} A ${r} ${r} 0 0 1 ${pied.x + r * 0.7} ${haut - 4 + r * 0.7}`}
            fill="none"
            stroke="#0EA5E9"
            strokeWidth="2"
            strokeLinecap="round"
            opacity={0.85 - i * 0.22}
          />
        ))}
    </g>
  );
}

/** Une voie ferrée, posée à plat sur la grille. */
export function VoieFerree({
  gx,
  gy,
  l,
}: {
  readonly gx: number;
  readonly gy: number;
  readonly l: number;
}) {
  const traverses: ReactNode[] = [];
  for (let t = 0.15; t < l; t += 0.34) {
    const a = proj(gx + t, gy);
    const b = proj(gx + t, gy + 0.7);
    traverses.push(
      <line key={t} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#A8A29E" strokeWidth="3" />,
    );
  }
  const rail = (decalage: number) => {
    const a = proj(gx, gy + decalage);
    const b = proj(gx + l, gy + decalage);
    return <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#78716C" strokeWidth="2.5" />;
  };
  return (
    <g aria-hidden="true">
      <polygon points={losange(gx, gy - 0.1, l, 0.9)} fill="#D6D3D1" />
      {traverses}
      {rail(0.18)}
      {rail(0.52)}
    </g>
  );
}

/** Un champ labouré : de quoi meubler la campagne autour de la ville. */
export function Champ({
  gx,
  gy,
  l,
  p,
  teinte = '#C3D98E',
}: Empreinte & { readonly teinte?: string }) {
  const sillons: ReactNode[] = [];
  for (let t = 0.2; t < l; t += 0.45) {
    const a = proj(gx + t, gy + 0.12);
    const b = proj(gx + t, gy + p - 0.12);
    sillons.push(
      <line
        key={t}
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={teinter(teinte, 0.9)}
        strokeWidth="2"
      />,
    );
  }
  return (
    <g aria-hidden="true">
      <polygon points={losange(gx, gy, l, p)} fill={teinte} />
      {sillons}
    </g>
  );
}

/** La hauteur d'une tuile, réexportée pour caler les étiquettes. */
export const HAUTEUR_TUILE = DEMI_HAUTEUR;
